import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, ButtonBase, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

const dividerSx = {
  width: '1px',
  minWidth: '1px',
  height: '25px',
  backgroundColor: '#E8E4DE',
};

const groupLabelSx = {
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '14px',
  color: '#6B6760',
  whiteSpace: 'nowrap',
};

const chipSx = (borderColor, backgroundColor) => ({
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'px': '9px',
  'py': '4px',
  'borderRadius': '20px',
  'border': `1px solid ${borderColor}`,
  'backgroundColor': backgroundColor,
  'whiteSpace': 'nowrap',
  'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
  },
});

// The accent chip is the secondary treatment, used where an option has no status colour of
// its own: the priority sort and the job type filter.
const accentChipSx = (isSelected) => {
  return chipSx(isSelected ? '#EBBDAD' : '#E8E4DE', isSelected ? '#FFF7F4' : '#F9F8F6');
};

const chipLabelSx = (isSelected) => ({
  fontFamily: 'Geist Mono',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '14px',
  color: isSelected ? '#DA7955' : '#6B6760',
});

const FilterBar = (props) => {
  const {
    selection,
    updateFilter,
    onClearFilter,
    onTogglePrioritySort,
    filterOptions,
  } = props;

  const statusFilter = filterOptions?.status;
  const jobTypeFilter = filterOptions?.jobType;
  const selectedStatus = selection?.filter?.status?.optionSelected ?? 'ACTIVE';
  const selectedJobType = selection?.filter?.jobType?.optionSelected ?? null;
  const sortByPriority = !!selection?.sortByPriority;
  const isDefaultSelection = selectedStatus === 'ACTIVE' && sortByPriority && !selectedJobType;
  const statusMap = translate('career.Status');
  const options = React.useMemo(() => {
    return Object.values(statusFilter?.options ?? {}).sort((a, b) => a.rank - b.rank);
  }, [statusFilter]);
  const jobTypeOptions = React.useMemo(() => {
    return Object.values(jobTypeFilter?.options ?? {}).sort((a, b) => a.rank - b.rank);
  }, [jobTypeFilter]);

  if (!statusFilter) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          alignContent: 'flex-start',
          width: '100%',
          gap: '12px 8px',
        }}
      >
        <Typography sx={groupLabelSx}>
          {translate('career.dashboard.JobListings.Filter.Status')}
        </Typography>
        <Box sx={dividerSx} />
        {
          options.map((option) => {
            const isSelected = option.key === selectedStatus;
            const selectedColors = statusMap[option.key] ?? statusMap.INACTIVE;

            return (
              <ButtonBase
                key={option.key}
                onClick={() => updateFilter(statusFilter, option)}
                sx={chipSx(
                    isSelected ? selectedColors.ColorBorder : '#E8E4DE',
                    isSelected ? selectedColors.Color : '#F9F8F6',
                )}
              >
                <Typography
                  sx={{
                    ...chipLabelSx(isSelected),
                    color: isSelected ? selectedColors.ColorText : '#6B6760',
                  }}
                >
                  {option.label}
                </Typography>
              </ButtonBase>
            );
          })
        }
        {
          selectedStatus === 'ACTIVE' &&
          <ButtonBase
            onClick={() => onTogglePrioritySort(!sortByPriority)}
            sx={accentChipSx(sortByPriority)}
          >
            <Typography sx={chipLabelSx(sortByPriority)}>
              {translate('career.dashboard.JobListings.Sort.Priority')}
            </Typography>
          </ButtonBase>
        }
        {
          jobTypeFilter &&
          <React.Fragment>
            <Box sx={dividerSx} />
            <Typography sx={groupLabelSx}>
              {jobTypeFilter.label}
            </Typography>
            {
              jobTypeOptions.map((option) => {
                const isSelected = option.key === selectedJobType;

                return (
                  <ButtonBase
                    key={option.key}
                    onClick={() => updateFilter(jobTypeFilter, isSelected ? null : option)}
                    sx={accentChipSx(isSelected)}
                  >
                    <Typography sx={chipLabelSx(isSelected)}>
                      {option.label}
                    </Typography>
                  </ButtonBase>
                );
              })
            }
          </React.Fragment>
        }
        <Box sx={dividerSx} />
        <ButtonBase
          onClick={onClearFilter}
          sx={chipSx('#E8E4DE', isDefaultSelection ? '#F9F8F6' : '#FFF7F4')}
        >
          <Typography
            sx={{
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '14px',
              color: isDefaultSelection ? '#6B6760' : '#DA7955',
            }}
          >
            <Translate expression='generic.ClearAllFilters' />
          </Typography>
        </ButtonBase>
      </Box>
    </Box>
  );
};

FilterBar.propTypes = {
  selection: PropTypes.object.isRequired,
  filterOptions: PropTypes.object.isRequired,
  updateFilter: PropTypes.func.isRequired,
  onClearFilter: PropTypes.func.isRequired,
  onTogglePrioritySort: PropTypes.func.isRequired,
};
const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(FilterBar));
